<template>
  <div class="new-credit-card">
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div v-if="!isApp" class="new-credit-card__header">
      <PageHeadline title="クレジットカードの追加" />
    </div>
    <div class="new-credit-card__description">
      <div class="new-credit-card__description__credit-cards">
        <CreditCardVisa />
        <CreditCardMastercard />
        <CreditCardJcb />
        <CreditCardAmerican />
        <CreditCardDiners />
      </div>
      <p class="caption">
        VISA、Master Card、JCB、AMERICAN
        EXPRESS、ダイナースクラブのカードをご利用いただけます。
      </p>
      <p class="box-list--attention">
        プリペイドカードおよび海外発行カードの場合、ご利用いただけない場合がございますので、日本国内発行のカードをご利用ください。
      </p>
    </div>
    <div class="new-credit-card__form">
      <div
        v-if="isShowableCreditCardNotice"
        class="new-credit-card__form__notice"
      >
        <p class="box-list--alert">
          {{ creditCardNotice }}
        </p>
      </div>
      <form
        name="credit_card_input_form"
        action="/credit_cards/submit"
        method="POST"
      >
        <input v-model="csrfToken" name="authenticity_token" type="hidden" />
        <div
          class="new-credit-card__form__number"
          :class="{
            field_with_errors: isNotEmptyValidateCreditCardNumberMessage
          }"
        >
          <p class="new-credit-card__form__number__label">
            カード番号
          </p>
          <input
            v-model.trim="creditCardNumber"
            type="text"
            name="credit_card_input[number]"
            maxlength="16"
            placeholder="0000 0000 0000 0000"
            autocomplete="cc-number"
            :class="{
              'input-form': isEmptyCreditCardNumber,
              'input-form--inputted': !isEmptyCreditCardNumber,
              'input-form--inputting': isFocusedCreditCardNumber
            }"
            @keyup="validateCreditCardNumber"
            @focus="onFocusCreditCardNumber"
            @blur="onBlurCreditCardNumber"
          />
          <p
            v-if="isNotEmptyValidateCreditCardNumberMessage"
            class="error-message"
          >
            {{ validateCreditCardNumberMessage }}
          </p>
        </div>
        <div
          class="new-credit-card__form__expiration"
          :class="{
            field_with_errors: isNotEmptyValidateCreditCardExpiration
          }"
        >
          <p class="new-credit-card__form__expiration__label">
            有効期限
          </p>
          <div class="new-credit-card__form__expiration__date">
            <div class="new-credit-card__form__expiration__date__month">
              <select
                v-model="creditCardExpirationMonth"
                name="credit_card_input[expiration_month]"
                autocomplete="cc-exp-month"
                :class="{
                  'select-form--selected': isSelectedMonth,
                  'select-form--unselected': !isSelectedMonth
                }"
                @change="validateCreditCardExpirationMonth"
              >
                <option disabled value="">
                  月
                </option>
                <option
                  v-for="month in selectableExpirationMonth"
                  :key="month"
                  :value="month"
                >
                  {{ month }}
                </option>
              </select>
            </div>
            <div class="new-credit-card__form__expiration__date__year">
              <select
                v-model="creditCardExpirationYear"
                name="credit_card_input[expiration_year]"
                autocomplete="cc-exp-year"
                :class="{
                  'select-form--selected': isSelectedYear,
                  'select-form--unselected': !isSelectedYear
                }"
                @change="validateCreditCardExpirationYear"
              >
                <option disabled value="">
                  年
                </option>
                <option
                  v-for="year in selectableExpirationYear"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
            </div>
          </div>
          <div
            v-if="validateCreditCardExpirationMonthMessage"
            class="error-message"
          >
            <span>{{ validateCreditCardExpirationMonthMessage }}</span>
          </div>
          <div
            v-if="validateCreditCardExpirationYearMessage"
            class="error-message"
          >
            <span>{{ validateCreditCardExpirationYearMessage }}</span>
          </div>
          <div v-if="validateCreditCardExpirationMessage" class="error-message">
            <span>{{ validateCreditCardExpirationMessage }}</span>
          </div>
        </div>
        <div
          class="new-credit-card__form__security-code"
          :class="{
            field_with_errors: isNotEmptyValidateCreditCardSecurityCodeMessage
          }"
        >
          <p class="new-credit-card__form__security-code__label">
            セキュリティコード
          </p>
          <input
            id="credit-card-security-code-input"
            v-model.trim="creditCardSecurityCode"
            class="new-credit-card__form__security-code__input"
            type="text"
            name="credit_card_input[security_code]"
            maxlength="4"
            placeholder="123"
            autocomplete="cc-csc"
            :class="{
              'input-form': isEmptyCreditCardSecurityCode,
              'input-form--inputted': !isEmptyCreditCardSecurityCode,
              'input-form--inputting': isFocusedCreditCardSecurityCode
            }"
            @keyup="validateCreditCardSecurityCode"
            @focus="onFocusCreditCardSecurityCode"
            @blur="onBlurCreditCardSecurityCode"
          />
          <p v-if="validateCreditCardSecurityCodeMessage" class="error-message">
            {{ validateCreditCardSecurityCodeMessage }}
          </p>
        </div>
        <div class="new-credit-card__form__description">
          <p class="minimal">
            カード裏面署名欄に印字されている右端3ケタの数字をご入力ください<br />
            （※AMERICAN EXPRESSの場合は、カード表面右上の4ケタの数字です）
          </p>
        </div>
        <input
          v-model="creditCardToken"
          type="hidden"
          name="credit_card_token[token]"
        />
        <input
          v-model="creditCardTokenKey"
          type="hidden"
          name="credit_card_token[token_key]"
        />
        <input
          v-model="creditCardTokenSecurityCode"
          type="hidden"
          name="credit_card_token[security_code]"
        />
        <input v-model="email" type="hidden" name="email" />
        <input
          v-model="tds2InfoToken"
          type="hidden"
          name="credit_card_token[tds2_info_token]"
        />
        <input
          v-model="tds2InfoTokenKey"
          type="hidden"
          name="credit_card_token[tds2_info_token_key]"
        />
        <div class="new-credit-card__form__submit">
          <div
            v-if="isNotOverFiveSeconds"
            class="new-credit-card__disabled-button"
          >
            <SmallLoading />
          </div>
          <button
            v-else
            id="credit-card-submit-button"
            type="button"
            class="btn btn--primary"
            :disabled="disableSubmitButton"
            @click="onClickSubmitButton"
          >
            登録する
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { csrfToken } from 'rails-ujs'
import axios from 'axios'
import CreditCardJcb from '../../atoms/credit-cards/CreditCardJcb'
import CreditCardMastercard from '../../atoms/credit-cards/CreditCardMastercard'
import CreditCardVisa from '../../atoms/credit-cards/CreditCardVisa'
import CreditCardAmerican from '../../atoms/credit-cards/CreditCardAmerican'
import CreditCardDiners from '../../atoms/credit-cards/CreditCardDiners'
import FeedbackError from '../../atoms/feedbacks/feedback-error'
import PageHeadline from '../../molecules/headlines/PageHeadline'
// TODO: Riskifiedの導入に伴いasukaの利用を停止する。3ヶ月の試用後に戻せるようコメントアウトにしているが、正式に不要になればvue側を含めて削除。
// import * as Sentry from '@sentry/browser'
import SmallLoading from '../../atoms/loadings/SmallLoading'

const CREDIT_CARD_NUMBER_REGEXP = /^\d{14,16}$/
const SECURITY_CODE_REGEXP = /^\d{3,4}$/
// TODO: asuka利用停止のためのコメントアウト。
// const ASUKA_SUCCESS_CODES = ['LRT', 'BIAT', 'MM']

export default {
  components: {
    SmallLoading,
    CreditCardJcb,
    CreditCardMastercard,
    CreditCardVisa,
    CreditCardAmerican,
    CreditCardDiners,
    FeedbackError,
    PageHeadline
  },
  props: {
    creditCardNotice: {
      type: String,
      required: false,
      default: ''
    },
    errorMessage: {
      type: String,
      required: false,
      default: ''
    },
    isApp: {
      type: Boolean,
      required: true
    },
    email: {
      type: String,
      required: true
    },
    // TODO: asuka利用停止のためのコメントアウト。
    // asukaJs: {
    //   type: String,
    //   required: true
    // },
    sbpsTds2TokenJs: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      csrfToken: '',
      creditCardNoticeMessage: '',
      showableCreditCardNoticeMessage: true,
      selectableExpirationMonth: [
        '01',
        '02',
        '03',
        '04',
        '05',
        '06',
        '07',
        '08',
        '09',
        '10',
        '11',
        '12'
      ],
      selectableExpirationYear: [],
      // form
      isFocusedCreditCardNumber: false,
      isFocusedCreditCardSecurityCode: false,
      isSubmitted: false,
      isNotOverFiveSeconds: true,
      // creditCard
      creditCardNumber: '',
      creditCardExpirationYear: '',
      creditCardExpirationMonth: '',
      creditCardSecurityCode: '',
      // creditCardToken
      creditCardToken: '',
      creditCardTokenKey: '',
      creditCardTokenSecurityCode: '',
      // 3D Secure 2.0 Token
      tds2InfoToken: '',
      tds2InfoTokenKey: '',
      // sbps settings
      merchant_id: '',
      service_id: '',
      // validate
      validateCreditCardNumberMessage: '',
      validateCreditCardExpirationMessage: '',
      validateCreditCardExpirationMonthMessage: '',
      validateCreditCardExpirationYearMessage: '',
      validateCreditCardSecurityCodeMessage: '',
      // feedback
      showableErrorFeedback: false
    }
  },
  computed: {
    isShowableCreditCardNotice() {
      return this.creditCardNotice.length !== 0
    },
    isEmptyCreditCardNumber() {
      return this.creditCardNumber.length === 0
    },
    isSelectedMonth() {
      return this.creditCardExpirationMonth !== ''
    },
    isSelectedYear() {
      return this.creditCardExpirationYear !== ''
    },
    isEmptyCreditCardSecurityCode() {
      return this.creditCardSecurityCode.length === 0
    },
    isEmptyAnyValueCreditCard() {
      return (
        this.isEmptyCreditCardNumber ||
        this.isEmptyCreditCardSecurityCode ||
        !this.isSelectedMonth ||
        !this.isSelectedYear
      )
    },
    isNotCollectCreditCardNumber() {
      return !CREDIT_CARD_NUMBER_REGEXP.test(this.creditCardNumber)
    },
    isNotEmptyValidateCreditCardNumberMessage() {
      return this.validateCreditCardNumberMessage.length !== 0
    },
    isNotCollectValueCreditCardExpirationMonth() {
      return !this.selectableExpirationMonth.some(
        v => v === this.creditCardExpirationMonth
      )
    },
    isNotCollectValueCreditCardExpirationYear() {
      return !this.selectableExpirationYear.some(
        v => v === this.creditCardExpirationYear
      )
    },
    isNotEmptyValidateCreditCardExpiration() {
      return (
        this.validateCreditCardExpirationMonthMessage.length !== 0 ||
        this.validateCreditCardExpirationYearMessage.length !== 0 ||
        this.validateCreditCardExpirationMessage.length !== 0
      )
    },
    isNotCollectCreditCardSecurityCode() {
      return !SECURITY_CODE_REGEXP.test(this.creditCardSecurityCode)
    },
    isNotEmptyValidateCreditCardSecurityCodeMessage() {
      return this.validateCreditCardSecurityCodeMessage.length !== 0
    },
    isNotEmptyAnyValidateMessages() {
      return (
        this.isNotEmptyValidateCreditCardNumberMessage ||
        this.isNotEmptyValidateCreditCardExpiration ||
        this.isNotEmptyValidateCreditCardSecurityCodeMessage
      )
    },
    disableSubmitButton() {
      return (
        this.isEmptyAnyValueCreditCard ||
        this.isNotEmptyAnyValidateMessages ||
        this.isSubmitted
      )
    }
  },
  mounted() {
    // NOTE: 不正対策のasukaのスクリプトを動的に埋め込む処理
    // TODO: asuka利用停止のためのコメントアウト。
    // const recaptchaScript = document.createElement('script')
    // recaptchaScript.setAttribute('src', this.asukaJs)
    // document.head.appendChild(recaptchaScript)

    // NOTE: SBPSの3Dセキュア2.0のスクリプトを動的に埋め込む処理
    const sbpsTds2TokenScript = document.createElement('script')
    sbpsTds2TokenScript.setAttribute('src', this.sbpsTds2TokenJs)
    document.head.appendChild(sbpsTds2TokenScript)

    this.csrfToken = csrfToken()
    this.selectableExpirationYear = this.createSelectableExpirationYears()
    this.creditCardNoticeMessage = this.creditCardNotice
    this.showableErrorFeedback = this.errorMessage.length !== 0
    this.waitFiveSeconds()
  },
  methods: {
    createSelectableExpirationYears() {
      const thisYear = new Date().getFullYear()
      return Array.from(Array(20).keys()).map(n => thisYear + n)
    },
    onFocusCreditCardNumber() {
      this.isFocusedCreditCardNumber = true
    },
    onBlurCreditCardNumber() {
      this.isFocusedCreditCardNumber = false
      this.validateCreditCardNumber()
    },
    onFocusCreditCardSecurityCode() {
      this.isFocusedCreditCardSecurityCode = true
    },
    onBlurCreditCardSecurityCode() {
      this.isFocusedCreditCardSecurityCode = false
      this.validateCreditCardSecurityCode()
    },
    async onClickSubmitButton() {
      this.isSubmitted = true
      this.creditCardNoticeMessage = ''
      // TODO: asuka利用停止のためのコメントアウト。
      // try {
      //   await this.checkScore()
      // } catch (error) {
      //   Sentry.captureException(error)
      //   if (this.isApp) {
      //     return
      //   }
      //   window.location.href = '/'
      // }
      await this.getSbpsSetting()
      await this.prepareTds2Token()
      await this.prepareToken()
      this.submitCreditCardInputForm()
    },
    async getSbpsSetting() {
      const res = await axios.get('sbps_setting')
      this.merchantId = res.data.merchant_id
      this.serviceId = res.data.service_id
    },
    async prepareTds2Token() {
      const res = await this.generateTds2Token()
      this.setTds2TokenToHiddenInput(res)
    },
    generateTds2Token() {
      return new Promise(resolve => {
        // eslint-disable-next-line no-undef
        com_sbps_system_tds2.generateToken(
          {
            merchantId: this.merchantId,
            serviceId: this.serviceId,
            billingLastName: '',
            billingFirstName: '',
            billingPostalCode: '',
            billingCity: '',
            billingAddress1: '',
            billingAddress2: '',
            billingAddress3: '',
            billingPhone: '',
            workPhone: '',
            shippingPostalCode: '',
            shippingCity: '',
            shippingAddress1: '',
            shippingAddress2: '',
            shippingAddress3: '',
            email: this.email
          },
          resolve
        )
      })
    },
    setTds2TokenToHiddenInput(response) {
      if (response.result === 'OK') {
        this.tds2InfoToken = response.tokenResponse.tds2infoToken
        this.tds2InfoTokenKey = response.tokenResponse.tds2infoTokenKey
      } else {
        this.creditCardNoticeMessage =
          'エラーが発生しました。 エラーコード ' + response.errorCode
      }
    },
    async prepareToken() {
      const res = await this.generateToken()
      this.setTokenToHiddenInput(res)
    },
    generateToken() {
      return new Promise(resolve => {
        // NOTE: com_sbps_systemという外部のJavaScriptを利用するため、eslintのno-undefをdisableしている
        // 外部JavaScriptはnew.html.slimで定義されている
        // eslint-disable-next-line no-undef
        com_sbps_system.generateToken(
          {
            merchantId: this.merchantId,
            serviceId: this.serviceId,
            ccNumber: this.creditCardNumber,
            ccExpiration:
              this.creditCardExpirationYear + this.creditCardExpirationMonth,
            securityCode: this.creditCardSecurityCode
          },
          resolve
        )
      })
    },
    setTokenToHiddenInput(response) {
      if (response.result === 'OK') {
        this.creditCardToken = response.tokenResponse.token
        this.creditCardTokenKey = response.tokenResponse.tokenKey
        this.creditCardTokenSecurityCode = this.creditCardSecurityCode
      } else {
        this.creditCardNoticeMessage =
          'エラーが発生しました。 エラーコード ' + response.errorCode
      }
    },
    submitCreditCardInputForm() {
      if (this.creditCardNoticeMessage.length === 0) {
        document.credit_card_input_form.submit()
        return
      }
      this.isSubmitted = false
      this.errorMessage =
        'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
    },
    validateCreditCardNumber() {
      if (this.isEmptyCreditCardNumber) {
        this.validateCreditCardNumberMessage = 'カード番号を入力してください。'
        return
      }
      if (this.isNotCollectCreditCardNumber) {
        this.validateCreditCardNumberMessage =
          'カード番号は14文字以上16文字以下の半角数字で入力してください。'
        return
      }
      this.validateCreditCardNumberMessage = ''
    },
    validateCreditCardExpirationMonth() {
      if (this.isNotCollectValueCreditCardExpirationMonth) {
        this.validateCreditCardExpirationMessage =
          '有効期限に設定されている値が不正です。'
        return
      }
      if (this.isSelectedYear) {
        this.validateCreditCardExpirationDate()
      }
      this.validateCreditCardExpirationMonthMessage = ''
    },
    validateCreditCardExpirationYear() {
      if (this.isNotCollectValueCreditCardExpirationYear) {
        this.validateCreditCardExpirationMessage =
          '有効期限に設定されている値が不正です。'
        return
      }
      if (this.isSelectedMonth) {
        this.validateCreditCardExpirationDate()
      }
      this.validateCreditCardExpirationMonthMessage = ''
    },
    validateCreditCardExpirationDate() {
      if (!this.isSelectedYear || !this.isSelectedMonth) {
        return false
      }
      const expiration = new Date(
        this.creditCardExpirationYear,
        this.creditCardExpirationMonth,
        0,
        23,
        59,
        59
      )
      if (expiration < new Date()) {
        this.validateCreditCardExpirationMessage =
          'このカードは有効期限が切れています。'
        return
      }
      this.validateCreditCardExpirationMessage = ''
    },
    validateCreditCardSecurityCode() {
      if (this.isEmptyCreditCardSecurityCode) {
        this.validateCreditCardSecurityCodeMessage =
          'セキュリティコードを入力してください。'
        return
      }
      if (this.isNotCollectCreditCardSecurityCode) {
        this.validateCreditCardSecurityCodeMessage =
          'セキュリティコードは3文字以上4文字以下の半角数字で入力してください。'
        return
      }
      this.validateCreditCardSecurityCodeMessage = ''
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    // TODO: asuka利用停止のためのコメントアウト。
    // checkScore() {
    //   return new Promise((resolve, reject) => {
    //     // eslint-disable-next-line no-undef
    //     aerosmith.checkScore(
    //       reason => {
    //         if (ASUKA_SUCCESS_CODES.includes(reason)) {
    //           resolve(reason)
    //         } else {
    //           reject(reason)
    //         }
    //       },
    //       reason => reject(reason)
    //     )
    //   })
    // },
    waitFiveSeconds() {
      setTimeout(() => {
        this.isNotOverFiveSeconds = false
      }, 5000)
    }
  }
}
</script>
<style lang="scss" scoped>
.new-credit-card {
  text-align: left;

  &__header {
    background-color: $white;
    margin-bottom: 16px;
  }

  &__description {
    background-color: $white;
    padding: 24px 16px;
    margin-bottom: 16px;

    & > * {
      width: $small-contents-width;
      margin: auto;

      @media screen and (max-width: $small-contents-width) {
        width: auto;
      }
    }

    & > .box-list--attention {
      box-sizing: border-box;
    }

    &__credit-cards {
      display: flex;
      padding-bottom: 16px;

      .credit-card {
        margin-right: 16px;

        &:last-of-type {
          margin-right: 0;
        }
      }
    }

    .caption {
      padding-bottom: 16px;
    }
  }

  &__form {
    background-color: $white;
    padding: 24px 16px;

    & > * {
      width: $small-contents-width;
      margin: auto;

      @media screen and (max-width: $small-contents-width) {
        width: auto;
      }
    }

    &__notice {
      padding-bottom: 24px;
    }

    &__number {
      padding-bottom: 24px;

      &__label {
        @extend %body;
        line-height: 1.6;
        padding-bottom: 8px;
      }

      .error-message {
        padding-bottom: 16px;
      }
    }

    &__expiration {
      padding-bottom: 24px;

      &__label {
        @extend %body;
        line-height: 1.6;
        padding-bottom: 8px;
      }

      &__date {
        display: flex;

        &__month {
          width: 64px;
          padding-right: 16px;
        }

        &__year {
          width: 80px;
        }
      }

      .error-message {
        padding-bottom: 16px;
      }
    }

    &__security-code {
      padding-bottom: 24px;

      &__label {
        @extend %body;
        line-height: 1.6;
        padding-bottom: 8px;
      }

      .error-message {
        padding-bottom: 16px;
      }

      &__input {
        width: 80px;
      }
    }

    &__description {
      padding-bottom: 64px;
    }

    &__submit {
      text-align: center;
      padding-bottom: 40px;
    }
  }

  &__disabled-button {
    width: 100%;
    height: 48px;
    padding: 16px;
    font-size: 1.6rem;
    font-weight: bold;
    line-height: 1.1;
    cursor: pointer;
    box-sizing: border-box;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: center;
    background-color: $gray-2;
    color: $gray-1;
    border-radius: 100px;
  }
}
</style>
